import type { BirdIconDef, BirdIconMotion } from '../types';
import { BIRD_ACCENT, BIRD_WING } from './shared';

function def(markup: string, motion: BirdIconMotion = 'wing'): BirdIconDef {
  return { markup, motion };
}

/** Pattern-based bird-themed icons for remaining Heroicon names */
export function heuristicBirdIcon(name: string): BirdIconDef {
  const n = name.replace(/Icon$/, '');

  if (/^Chevron/.test(n)) {
    const dir = n.replace('Chevron', '').toLowerCase();
    const paths: Record<string, string> = {
      down: '<path d="M7 10l5 5 5-5"/>',
      up: '<path d="M7 14l5-5 5 5"/>',
      left: '<path d="M14 7l-5 5 5 5"/>',
      right: '<path d="M10 7l5 5-5 5"/>',
    };
    return def(`${paths[dir] ?? paths.down}${BIRD_ACCENT}`, 'flutter');
  }

  if (/^Arrow/.test(n) || /^Arrows/.test(n)) {
    return def(`<path d="M5 12h12"/><path d="M13 8l4 4-4 4"/>${BIRD_WING}`, 'flutter');
  }

  if (/Document|Clipboard|Folder|Newspaper|Book/.test(n)) {
    return def(
      `<rect x="7" y="4" width="11" height="16" rx="1.2"/><path d="M10 9h6M10 12h4"/>${BIRD_ACCENT}`,
      'peck',
    );
  }

  if (/User|Identification|Users/.test(n)) {
    return def(
      `<circle cx="11" cy="9" r="2.2"/><path d="M6 17c0-2 2.2-3.5 5-3.5s5 1.5 5 3.5"/><ellipse cx="17.5" cy="6.5" rx="1.1" ry=".8" fill="currentColor"/>`,
      'bob',
    );
  }

  if (/Chart|Presentation|Graph|Trending/.test(n)) {
    return def(`<path d="M4 18V6m0 12h14"/><path d="M7 15l3-4 3 2 4-6"/>${BIRD_ACCENT}`, 'pulse');
  }

  if (/Bank|Currency|Receipt|Credit|Calculator|Scale/.test(n)) {
    return def(
      `<rect x="4" y="7" width="16" height="10" rx="1.5"/><circle cx="12" cy="12" r="2"/><ellipse cx="18" cy="6" rx="1.1" ry=".8" fill="currentColor"/>`,
      'bob',
    );
  }

  if (/Building|Home|Office|Library|Store/.test(n)) {
    return def(
      `<path d="M6 19V8l6-3 6 3v11"/><circle cx="15" cy="7" r="1" fill="currentColor"/>`,
      'bob',
    );
  }

  if (/Bell|Notification/.test(n)) {
    return def(
      `<path d="M12 4a4 4 0 0 0-4 4v2.5c0 .9-.3 1.8-.9 2.5L6 15.5h12l-1.1-2.5"/><path d="M10 17a2 2 0 0 0 4 0"/>`,
      'bob',
    );
  }

  if (/Heart|Health|Shield|Beaker|Medical/.test(n)) {
    return def(
      `<path d="M12 18s-5-3.2-5-7.2C7 8 9 6.5 12 6.5s5 1.5 5 4.3c0 4-5 7.2-5 7.2z"/><path d="M9.5 9c.8-.9 2.2-.9 3 0" fill="none"/>`,
      'pulse',
    );
  }

  if (/Photo|Camera|Film|Image/.test(n)) {
    return def(
      `<rect x="4" y="6" width="16" height="13" rx="2"/><circle cx="9" cy="11" r="1.4"/><path d="M4 16l4-3 3 2 4-4 5 5"/>${BIRD_ACCENT}`,
      'pulse',
    );
  }

  if (/Phone|Device|Mobile|Envelope|Chat|AtSymbol/.test(n)) {
    return def(`<rect x="7" y="5" width="10" height="14" rx="2"/><path d="M10 16h4"/>${BIRD_ACCENT}`, 'bob');
  }

  if (/Map|Globe|Location|Pin/.test(n)) {
    return def(`<path d="M12 21s6-4.5 6-9a6 6 0 1 0-12 0c0 4.5 6 9 6 9z"/><circle cx="12" cy="12" r="2"/>`, 'bob');
  }

  if (/Cloud|Server|Cpu|Wifi|Signal|Bolt|Fire/.test(n)) {
    return def(`<path d="M7 16h10a3 3 0 0 0 .2-6 4.5 4.5 0 0 0-8.6-1.5A3.5 3.5 0 0 0 7 16z"/>${BIRD_WING}`, 'wing');
  }

  if (/Cog|Adjust|Wrench|Tool|Command/.test(n)) {
    return def(
      `<circle cx="12" cy="12" r="2.5"/><path d="M12 4v2m0 12v2M4 12h2m12 0h2"/>${BIRD_ACCENT}`,
      'pulse',
    );
  }

  if (/Check|XCircle|XMark|Minus|Plus|NoSymbol/.test(n)) {
    if (n.includes('Plus')) {
      return def(`<path d="M12 6v12M6 12h12"/>${BIRD_ACCENT}`, 'bob');
    }
    if (n.includes('X')) {
      return def(`<path d="M8 8l8 8M16 8l-8 8"/>${BIRD_ACCENT}`, 'flutter');
    }
    return def(`<path d="M8 12.5h8"/><circle cx="17" cy="7" r="1" fill="currentColor"/>`, 'peck');
  }

  if (/Eye/.test(n)) {
    return def(
      `<path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6"/><circle cx="12" cy="12" r="2"/>${BIRD_ACCENT}`,
      'pulse',
    );
  }

  if (/Star|Sparkle|Trophy|Gift|Tag|Ticket/.test(n)) {
    return def(`<path d="M12 4l1.2 4.2 4.2 1.2-4.2 1.2L12 15l-1.2-4.2-4.2-1.2 4.2-1.2z"/>${BIRD_ACCENT}`, 'flutter');
  }

  if (/Calendar|Clock/.test(n)) {
    return def(`<rect x="5" y="6" width="14" height="13" rx="1.5"/><path d="M8 4v3M16 4v3M5 10h14"/>${BIRD_ACCENT}`, 'pulse');
  }

  if (/Table|Square|ViewColumn|Queue|List|Bars/.test(n)) {
    return def(`<path d="M5 6h14v4H5zm0 6h14v4H5z"/>${BIRD_ACCENT}`, 'peck');
  }

  if (/Archive|Tray|Inbox|Paper|Printer/.test(n)) {
    return def(`<path d="M5 8h14v10H5z"/><path d="M9 8V6h6v2"/>${BIRD_ACCENT}`, 'bob');
  }

  if (/Share|Link|PaperAirplane|Rocket/.test(n)) {
    return def(`<path d="M5 12l12-4-4 12-2-5-6-3z"/>${BIRD_WING}`, 'flutter');
  }

  if (/Play|Pause|Forward|Backward|Musical/.test(n)) {
    return def(`<path d="M9 7v10l8-5z"/>${BIRD_ACCENT}`, 'bob');
  }

  if (/Academic|LightBulb|Book|Code|Hashtag|Puzzle/.test(n)) {
    return def(`<path d="M12 4a6 6 0 0 0-4 10.5V17h8v-2.5A6 6 0 0 0 12 4z"/>${BIRD_ACCENT}`, 'peck');
  }

  if (/Sun|Moon|Swatch|Paint/.test(n)) {
    return def(`<circle cx="12" cy="12" r="4"/><path d="M12 4v2m0 12v2M4 12h2m12 0h2"/>`, 'pulse');
  }

  if (/Truck|Cart|Shopping|Cube/.test(n)) {
    return def(`<path d="M5 9h10v8H5z"/><path d="M15 10h3l2 3v4h-5"/>${BIRD_WING}`, 'flutter');
  }

  if (/Exclamation|Information|Question|Face/.test(n)) {
    return def(`<circle cx="12" cy="12" r="8"/><path d="M12 8v5"/><circle cx="12" cy="16" r=".8" fill="currentColor"/>`, 'bob');
  }

  // Default: perched bird silhouette
  return def(
    `<path d="M6 16c2-4 5-6 9-6s7 2 9 6"/><circle cx="15" cy="8" r="1.5" fill="currentColor"/><path d="M13.5 9.2c.9.8 2.1.8 3 0" stroke-width="1.2"/><path d="M5 13c1.5-2 3.5-3 6-3" fill="none"/>`,
    'wing',
  );
}
